/**
 * Sistema centralizado de logs de debug
 * Todos os logs são automaticamente desabilitados em produção
 */

const DEBUG_CATEGORIES = {
  PERF: 'PERF',
  POPOVER: 'POPOVER',
  FONT: 'FONT',
  PERSONALIZADOS: 'PERSONALIZADOS',
  SEARCH: 'SEARCH',
  IMPRESSAO: 'IMPRESSAO',
  ALL: 'ALL',
} as const;

type DebugCategory = typeof DEBUG_CATEGORIES[keyof typeof DEBUG_CATEGORIES];

// Verificar se debug está habilitado via localStorage ou env
const isDebugEnabled = (category?: DebugCategory): boolean => {
  if (!import.meta.env.DEV) {
    return false; // Sempre desabilitado em produção
  }

  // Em desenvolvimento, pode ser controlado via localStorage
  if (typeof window !== 'undefined') {
    const debugSetting = localStorage.getItem('debug');
    if (debugSetting === 'false') {
      return false;
    }
    if (category && debugSetting) {
      const enabledCategories = debugSetting.split(',');
      return enabledCategories.includes(category) || enabledCategories.includes('ALL');
    }
  }

  return true; // Padrão: habilitado em desenvolvimento
};

/**
 * Log de debug condicional
 * Só loga em desenvolvimento e se a categoria estiver habilitada
 */
export const debugLog = (category: DebugCategory, ...args: any[]): void => {
  if (isDebugEnabled(category)) {
    console.log(`[${category}]`, ...args);
  }
};

/**
 * Log de warn condicional
 */
export const debugWarn = (category: DebugCategory, ...args: any[]): void => {
  if (isDebugEnabled(category)) {
    console.warn(`[${category}]`, ...args);
  }
};

/**
 * Log de error (sempre loga, mas com prefixo condicional)
 */
export const debugError = (category: DebugCategory, ...args: any[]): void => {
  if (isDebugEnabled(category)) {
    console.error(`[${category}]`, ...args);
  } else {
    // Em produção, ainda loga erros mas sem prefixo de categoria
    console.error(...args);
  }
};

/**
 * Log de grupo condicional
 */
export const debugGroup = (category: DebugCategory, label: string, ...args: any[]): void => {
  if (isDebugEnabled(category)) {
    console.group(`[${category}] ${label}`, ...args);
  }
};

/**
 * Fechar grupo de debug
 */
export const debugGroupEnd = (category: DebugCategory): void => {
  if (isDebugEnabled(category)) {
    console.groupEnd();
  }
};

/**
 * Log de tabela condicional
 */
export const debugTable = (category: DebugCategory, data: any): void => {
  if (isDebugEnabled(category)) {
    console.table(data);
  }
};

export { DEBUG_CATEGORIES };
